import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InlineEntityList } from '@/components/roros/InlineEntityList';
import type { InlineFieldDef } from '@/components/roros/InlineEntityList';
import { useAttachmentThumbnail, useAttachmentById } from '@/components/roros/useAttachmentThumbnail';
import { AttachmentPreviewModal } from '@/components/Attachments/AttachmentPreviewModal';
import { AttachmentsService } from '@/services/AttachmentsService';
import { ImageIcon } from 'lucide-react';
import { t } from '@/lib/i18n';
import { useOverarchingEditable } from '../OverarchingEditableContext';

// ─── Title+description sections (Core Values / Principles / USPs) ────────────

export type TitledItem = {
  id: string;
  title: string;
  description: string | null;
  sortOrder: number;
  imageAttachmentId?: string | null;
};

const sectionRowAttachmentsService = new AttachmentsService();

function SectionRowThumb({ id }: { id: string | null }) {
  const url = useAttachmentThumbnail(id);
  const item = useAttachmentById(id);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<typeof item>(null);

  if (!id) {
    return (
      <div
        className="w-16 h-16 rounded-md border-2 border-dashed border-border flex items-center justify-center flex-shrink-0"
        aria-label={t('No image')}
      >
        <ImageIcon className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }

  const openPreview = () => {
    if (item) {
      setPreviewItem(item);
      setPreviewOpen(true);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={openPreview}
        disabled={!item}
        className="w-16 h-16 p-0 rounded-md overflow-hidden border border-border bg-muted flex items-center justify-center flex-shrink-0 hover:border-primary/50 transition-colors"
        aria-label={t('Open image preview')}
      >
        {url ? (
          <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <ImageIcon className="h-5 w-5 text-muted-foreground" />
        )}
      </Button>
      <AttachmentPreviewModal
        item={previewOpen ? previewItem : null}
        onClose={() => setPreviewOpen(false)}
        onUpdate={async (data) => {
          if (!previewItem) return;
          await sectionRowAttachmentsService.update(previewItem.id, data);
          setPreviewItem((prev) => (prev ? { ...prev, ...data } : null));
        }}
      />
    </>
  );
}

export function TitledListSection<T extends TitledItem>({
  title,
  subtitle,
  emptyHint,
  items,
  useCreate,
  useUpdate,
  useRemove,
  useReorder,
  includeImage = false,
}: {
  title: string;
  /** Optional subtitle rendered below the section heading. DOGFOOD-6. */
  subtitle?: string;
  emptyHint?: string;
  items: T[];
  useCreate: () => { mutateAsync: (p: Record<string, unknown>) => Promise<unknown> };
  useUpdate: () => { mutateAsync: (args: { id: string; payload: Record<string, unknown> }) => Promise<unknown> };
  useRemove: () => { mutateAsync: (id: string) => Promise<unknown> };
  useReorder: () => { mutateAsync: (items: { id: string; sortOrder: number }[]) => Promise<unknown> };
  includeImage?: boolean;
}) {
  const createMut = useCreate();
  const updateMut = useUpdate();
  const removeMut = useRemove();
  const reorderMut = useReorder();
  const { canEdit } = useOverarchingEditable();

  const fields: InlineFieldDef<T>[] = [
    { name: 'title' as keyof T & string, label: t('Title'), type: 'text', required: true },
    {
      name: 'description' as keyof T & string,
      label: t('Description'),
      type: 'textarea',
      // When an image sits next to the form, grow the description so the
      // left column roughly matches the square image column height.
      rows: includeImage ? 7 : 2,
    },
    ...(includeImage
      ? [{ name: 'imageAttachmentId' as keyof T & string, label: t('Image'), type: 'image' as const }]
      : []),
  ];

  async function handleReorder(index: number, direction: 'up' | 'down') {
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    await reorderMut.mutateAsync([
      { id: items[index].id, sortOrder: items[swapIdx].sortOrder },
      { id: items[swapIdx].id, sortOrder: items[index].sortOrder },
    ]);
  }

  return (
    <Card padding="md">
      <InlineEntityList<T>
        title={title}
        subtitle={subtitle}
        emptyHint={emptyHint}
        items={items}
        fields={fields}
        readOnly={!canEdit}
        onCreate={async (draft) => { await createMut.mutateAsync(draft as Record<string, unknown>); }}
        onUpdate={async (id, patch) => { await updateMut.mutateAsync({ id, payload: patch as Record<string, unknown> }); }}
        onDelete={async (id) => { await removeMut.mutateAsync(id); }}
        onReorder={handleReorder}
        renderRow={(item) => (
          <div className="flex items-start gap-3 min-w-0">
            {includeImage && <SectionRowThumb id={item.imageAttachmentId ?? null} />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{item.title}</p>
              {item.description && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.description}</p>
              )}
            </div>
          </div>
        )}
      />
    </Card>
  );
}
